import React, { useState } from "react";
import { toast } from "react-toastify";
import { apiService } from '../../../../services/api';
import { Empenho, EtapaEmpenho } from '../../../../types/empenhos';

interface FormCancelarEmpenhoProps {
  empenho: Empenho;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FormCancelarEmpenho: React.FC<FormCancelarEmpenhoProps> = ({
  empenho,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [observacoes, setObservacoes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Função para obter os efeitos do cancelamento baseado na etapa atual (ATUALIZADO - Novo Fluxo + Legado)
  const getEfeitosCancelamento = (etapa: EtapaEmpenho): string[] => {
    const efeitos: string[] = [];

    switch (etapa) {
      case 'COMPRAS':
        efeitos.push('O empenho será marcado como CANCELADO');
        efeitos.push('A Solicitação de Compra em andamento será cancelada');
        efeitos.push('⚠️ Se já houver confirmação do fornecedor, poderá haver multas');
        break;

      case 'PAGAMENTO':
        efeitos.push('O empenho será marcado como CANCELADO');
        efeitos.push('A Conta a Pagar será cancelada');
        efeitos.push('Todas as parcelas serão removidas');
        efeitos.push('Os dados bancários serão mantidos');
        efeitos.push('⚠️ Se já houver pagamentos parciais, será necessário estorno financeiro manual');
        break;

      case 'PAGO':
        efeitos.push('O empenho será marcado como CANCELADO');
        efeitos.push('A Conta a Pagar será cancelada');
        efeitos.push('⚠️ Se já houver pagamentos confirmados, será necessário estorno financeiro manual');
        break;

      case 'NF_SAIDA':
        efeitos.push('O empenho será marcado como CANCELADO');
        efeitos.push('A Conta a Receber será cancelada');
        efeitos.push('Os dados da NF serão limpos');
        efeitos.push('A Conta a Pagar será cancelada');
        efeitos.push('A Solicitação de Compra será cancelada');
        efeitos.push('⚠️ Se as contas já foram pagas/recebidas, será necessário estorno financeiro manual');
        break;

      case 'ENTREGA':
        efeitos.push('O empenho será marcado como CANCELADO');
        efeitos.push('O estoque será revertido (itens voltam ao estoque)');
        efeitos.push('A Conta a Receber será cancelada');
        efeitos.push('Os dados da NF e entrega serão limpos');
        efeitos.push('A Conta a Pagar será cancelada');
        efeitos.push('A Solicitação de Compra será cancelada');
        efeitos.push('⚠️ Se as contas já foram pagas/recebidas, será necessário estorno financeiro manual');
        break;

      default:
        efeitos.push('O empenho será marcado como CANCELADO');
    }

    return efeitos;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!observacoes.trim()) {
      toast.error('Por favor, informe o motivo do cancelamento');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiService.cancelarEmpenho({
        id_empenho: empenho.id,
        motivo: observacoes.trim() // CORRIGIDO: backend espera "motivo", não "observacoes"
      });

      if (response.success) {
        toast.success('Empenho cancelado com sucesso!');
        setObservacoes('');
        onSuccess();
      } else {
        throw new Error(response.message || 'Erro ao cancelar empenho');
      }
    } catch (error: any) {
      console.error('Erro ao cancelar empenho:', error);
      toast.error(error.message || 'Erro ao cancelar empenho');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setObservacoes('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const efeitos = getEfeitosCancelamento(empenho.etapa);

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                <i className="ti ti-alert-triangle me-2" />
                Cancelar Empenho
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                disabled={isSubmitting}
              />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <strong>Atenção!</strong> Esta ação não pode ser desfeita facilmente.
                </div>

                {/* Informações do empenho */}
                <div className="mb-4">
                  <h6 className="mb-2">Empenho a ser cancelado:</h6>
                  <div className="card bg-light">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Código:</strong> {empenho.codigo_empenho || `#${empenho.id}`}
                        </div>
                        <div className="col-md-6">
                          <strong>Etapa Atual:</strong>{' '}
                          <span className="badge badge-soft-info">{empenho.etapa}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Efeitos do cancelamento */}
                <div className="mb-4">
                  <h6 className="mb-2">
                    <i className="ti ti-info-circle me-2" />
                    Efeitos do cancelamento:
                  </h6>
                  <ul className="list-group">
                    {efeitos.map((efeito, index) => (
                      <li
                        key={index}
                        className={`list-group-item ${
                          efeito.includes('⚠️') ? 'list-group-item-warning' : ''
                        }`}
                      >
                        {efeito}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Motivo do cancelamento */}
                <div className="mb-3">
                  <label htmlFor="observacoes" className="form-label">
                    Motivo do Cancelamento <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="observacoes"
                    className="form-control"
                    rows={4}
                    placeholder="Descreva o motivo do cancelamento..."
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                  <div className="form-text">
                    Mínimo 10 caracteres. Este motivo ficará registrado no histórico.
                  </div>
                </div>

                {/* Confirmação */}
                <div className="alert alert-danger mb-0">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="confirmar"
                      required
                      disabled={isSubmitting}
                    />
                    <label className="form-check-label" htmlFor="confirmar">
                      Confirmo que li e entendi os efeitos do cancelamento
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  <i className="ti ti-x me-2" />
                  Voltar
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={isSubmitting || observacoes.trim().length < 10}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Confirmar Cancelamento
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={handleClose} />
    </>
  );
};

export default FormCancelarEmpenho;
